import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import LayoutWithMap from 'containers/Base/LayoutWithMap';
import { getUserAssignedDepartmentList } from 'reducers/users';
import useFilters from 'containers/Base/useFilters.js';
import {
  emergencySelectors,
  emergencyPageActions,
} from 'containers/Pages/Tasks/Emergencies';
import { actions as emergencyActions } from '../Modules/dataGridTable';
import { filters } from 'containers/Pages/Tasks/Emergencies/filters';
import { actions as emergencyFilterActions } from '../Modules/filters';
import { detailOpenState } from 'reducers/ui';
import useBusinessClient from 'containers/Pages/People/BusinessClients/Layouts/useBusinessClient';
import { PAGE_URLS } from 'utils/constants';

const Emergencies = ({
  columns,
  filtersRequired,
  details,
  loading,
  downloadable,
}) => {
  // useStates
  const [emergencyGridAPI, setEmergencyGridAPI] = useState();
  //---------------------------Selectors---------------------------
  const detailOpen = useSelector(state => detailOpenState(state));
  const emergencyListData = useSelector(state =>
    emergencySelectors.getEmergencyListTransformed(state),
  );
  const totalEntries = useSelector(state =>
    emergencySelectors.getTotalEntries(state),
  );
  const filterValues = useSelector(state =>
    emergencySelectors.getEmergencyFilters(state),
  );
  const forceRefresh = useSelector(state =>
    emergencySelectors.getForceRefresh(state),
  );
  const userAssignedDepartments = useSelector(state =>
    getUserAssignedDepartmentList(state),
  );
  const bizClients = useBusinessClient();
  const onDownloadClick = () => {
    dispatchPrintEmergencyList({ filters: localFilterValues });
  };
  const onMapSelectedEmergencyId = useSelector(state =>
    emergencySelectors.getSelectedRowID(state),
  );
  //--------------------------customHooks--------------------------
  const { localFilterValues, updateFilter } = useFilters({
    filterValues,
  });
  //---------------------------Actions------------------------------
  const dispatch = useDispatch();
  const dispatchFetchEmergenciesList = useCallback(
    ({ filters, from }) =>
      dispatch(emergencyActions.fetchGridList({ filters, from })),
    [dispatch],
  );
  const dispatchPrintEmergencyList = useCallback(
    ({ filters }) => {
      dispatch(emergencyPageActions.printEmergencyList({ filters }));
    },
    [dispatch],
  );

  const fetchEmergencies = from => {
    dispatchFetchEmergenciesList({
      filters: localFilterValues,
      from,
    });
  };
  const onUpdate = () => {
    dispatchFetchEmergenciesList({ filters: localFilterValues });
  };
  const gridAPIHandler = gridAPI => {
    setEmergencyGridAPI(gridAPI);
  };
  const emergencyHighlight = onMapSelectedEmergencyId => {
    if (onMapSelectedEmergencyId != null) {
      const node =
        emergencyGridAPI &&
        emergencyGridAPI.getRowNode(onMapSelectedEmergencyId);
      // Go to the highlighted node
      emergencyGridAPI &&
        emergencyGridAPI.ensureNodeVisible(node, { position: 'top' });
      if (node) {
        if (!node.isSelected()) {
          node.setSelected(true);
        }
      }
    }
  };
  emergencyHighlight(onMapSelectedEmergencyId);

  return (
    <LayoutWithMap
      header="Emergencies"
      cols={columns}
      data={emergencyListData}
      totalEntries={totalEntries}
      loading={loading}
      forceRefresh={forceRefresh}
      //details
      DetailComponent={details}
      //creating and editing
      editable={false}
      //filters
      filtersRequired={filtersRequired}
      filterValues={localFilterValues}
      filters={filters({
        bizClients,
        userAssignedDepartments,
      })}
      forceOpenDetailPanel={detailOpen[PAGE_URLS.EMERGENCIES.link]}
      downloadable={downloadable}
      onDownloadClick={onDownloadClick}
      setFilters={(values, hasApplyFilter) =>
        updateFilter({
          values,
          hasApplyFilter,
          applyFilter: filters => {
            emergencyFilterActions.setFilterKey({ key: true });
            emergencyFilterActions.setFilters({ filters });
          },
        })
      }
      //end-filters
      onUpdate={onUpdate}
      fetchData={fetchEmergencies}
      setGridAPI={gridAPIHandler}
    />
  );
};
Emergencies.propTypes = {
  columns: PropTypes.array.isRequired,
  details: PropTypes.func.isRequired,
  filtersRequired: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  FormComponent: PropTypes.elementType,
  userAssignedDepartments: PropTypes.array,
  downloadable: PropTypes.bool,
};

export default Emergencies;
